import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useYear } from '../contexts/YearContext';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Loader2 } from 'lucide-react';

/**
 * Dashboard "Income vs Buffer Trend" chart.
 *
 * One combined card replacing the previously stacked Income Trend and Buffer
 * Trend. Plots two series per period from /api/dashboard/income-timeseries:
 *
 *   • Total Income  = advances + overrides + monthly checks   (green area)
 *   • Buffer Added  = first-class Buffer bucket                (cyan line)
 *
 * Plus a dashed horizontal reference for True Minimum weekly target. Buffer
 * values come straight from the server; the endpoint applies the canonical
 * writer-v2 / legacy fallback so Buffer is never derived from Savings here.
 *
 * Read-only. No allocation math, no engine/export side-effects.
 */

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const statusFor = (total, target) => {
  if (!target || target <= 0) {
    if (total > 0) return { label: 'Income Logged', tone: 'text-emerald-300' };
    return { label: 'No Income', tone: 'text-gray-400' };
  }
  if (total >= target) return { label: 'Above Target', tone: 'text-emerald-300' };
  if (total > 0) return { label: 'Below Target', tone: 'text-amber-300' };
  return { label: 'No Income', tone: 'text-gray-400' };
};

function CustomTooltip({ active, payload, label, granularity, target }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  const periodLabel =
    granularity === 'month' ? `${label} ${p.period_start?.slice(0, 4) || ''}` : label;
  const status = statusFor(p.total, target);
  return (
    <div
      className="rounded-lg border border-gray-700 bg-gray-900/95 backdrop-blur-sm px-4 py-3 shadow-xl"
      data-testid="income-buffer-tooltip"
    >
      <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
        {periodLabel}
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center justify-between gap-6 text-gray-200">
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            Total Income
          </span>
          <span className="font-semibold text-white">{formatCurrency(p.total)}</span>
        </div>
        <div className="flex items-center justify-between gap-6 text-gray-200">
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-cyan-400" />
            Buffer Generated
          </span>
          <span className="font-semibold text-white">{formatCurrency(p.buffer)}</span>
        </div>
        <div className="border-t border-gray-700/70 my-2" />
        <div className="flex items-center justify-between gap-6 text-gray-400">
          <span>Advances</span>
          <span className="text-gray-200">{formatCurrency(p.advances)}</span>
        </div>
        <div className="flex items-center justify-between gap-6 text-gray-400">
          <span>Overrides</span>
          <span className="text-gray-200">{formatCurrency(p.overrides)}</span>
        </div>
        <div className="flex items-center justify-between gap-6 text-gray-400">
          <span>Monthly Checks</span>
          <span className="text-gray-200">{formatCurrency(p.monthly)}</span>
        </div>
        {target ? (
          <div className="flex items-center justify-between gap-6 text-gray-400">
            <span>True Minimum</span>
            <span className="text-gray-200">{formatCurrency(target)}</span>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-6 pt-1">
          <span className="text-gray-400">Status</span>
          <span className={`font-semibold ${status.tone}`} data-testid="income-buffer-tt-status">
            {status.label}
          </span>
        </div>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-xs text-gray-300" data-testid="income-buffer-legend">
      <span className="flex items-center gap-2">
        <span className="inline-block w-3 h-0.5 bg-emerald-500 rounded" />
        Total Income
      </span>
      <span className="flex items-center gap-2">
        <span className="inline-block w-3 h-0.5 bg-cyan-400 rounded" />
        Buffer Generated
      </span>
      <span className="flex items-center gap-2">
        <span
          className="inline-block w-3 h-0.5 rounded"
          style={{
            backgroundImage:
              'linear-gradient(to right, #94a3b8 0, #94a3b8 50%, transparent 50%, transparent 100%)',
            backgroundSize: '6px 1px',
          }}
        />
        True Minimum
      </span>
    </div>
  );
}

function IncomeTrendChart() {
  const { selectedYear } = useYear();
  const [granularity, setGranularity] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dashboard/income-timeseries`, {
          params: { year: selectedYear, granularity },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error('Income/Buffer trend load failed:', err);
        if (!cancelled) setData({ points: [], target: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedYear, granularity]);

  const hasData = useMemo(
    () => data?.points?.some((p) => (p.total || 0) > 0 || (p.buffer || 0) > 0),
    [data]
  );

  const { totalIncome, bufferYtd, peak } = useMemo(() => {
    if (!data?.points) return { totalIncome: 0, bufferYtd: 0, peak: 0 };
    return {
      totalIncome: data.points.reduce((s, p) => s + (p.total || 0), 0),
      bufferYtd: data.points.reduce((s, p) => s + (p.buffer || 0), 0),
      peak: Math.max(...data.points.map((p) => p.total || 0), 0),
    };
  }, [data]);

  return (
    <div
      className="bg-gradient-to-br from-slate-900 to-gray-900 rounded-2xl shadow-xl p-6 border border-gray-800"
      data-testid="income-trend-chart"
    >
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Income vs Buffer Trend</h2>
          </div>
          <p className="text-sm text-gray-400">
            Compare total income against unallocated capital generated over time · {selectedYear}
          </p>
          <div className="flex items-end gap-6 mt-3 flex-wrap">
            <div>
              <p className="text-2xl font-bold text-white leading-tight">
                ${Number(totalIncome).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              <p className="text-xs text-emerald-400 mt-0.5">Income YTD</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white leading-tight">
                ${Number(bufferYtd).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              <p className="text-xs text-cyan-400 mt-0.5">Buffer YTD</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-3">
          <div
            className="inline-flex rounded-lg bg-gray-800 border border-gray-700 p-1"
            role="tablist"
            data-testid="income-trend-granularity"
          >
            {['week', 'month'].map((g) => (
              <button
                key={g}
                type="button"
                role="tab"
                aria-selected={granularity === g}
                onClick={() => setGranularity(g)}
                data-testid={`income-trend-${g}-btn`}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${
                  granularity === g
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {g === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>
          <Legend />
        </div>
      </div>

      <div className="h-64 w-full" style={{ minHeight: 256 }}>
        {loading && (
          <div className="h-full flex items-center justify-center text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {!loading && !hasData && (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <TrendingUp className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">No income logged for {selectedYear} yet.</p>
            <p className="text-xs mt-1">
              Log a paycheck on the Weekly Tracker to see your trend.
            </p>
          </div>
        )}
        {!loading && hasData && (
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <ComposedChart
              data={data.points}
              margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                vertical={false}
                stroke="#1f2937"
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="label"
                stroke="#6b7280"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={20}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => formatCurrency(v)}
                width={60}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    granularity={granularity}
                    target={
                      data?.target && granularity === 'month'
                        ? data.target * 4.33
                        : data?.target
                    }
                  />
                }
                cursor={{
                  stroke: '#10b981',
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                }}
              />
              {/* Reference line — True Minimum target. In Month mode we
                  scale the weekly target up by 4.33 weeks so the line is
                  comparable to the monthly buckets being plotted. */}
              {(() => {
                if (!(data?.target && data.target > 0)) return null;
                const refValue =
                  granularity === 'month' ? data.target * 4.33 : data.target;
                if (refValue >= peak * 2) return null;
                return (
                  <ReferenceLine
                    y={refValue}
                    stroke="#94a3b8"
                    strokeDasharray="5 5"
                    strokeWidth={1.25}
                    label={{
                      value: `Target ${formatCurrency(refValue)}${
                        granularity === 'month' ? '/mo' : '/wk'
                      }`,
                      position: 'insideTopRight',
                      fill: '#94a3b8',
                      fontSize: 11,
                    }}
                  />
                );
              })()}
              <Area
                type="monotone"
                dataKey="total"
                name="Total Income"
                stroke="#10b981"
                strokeWidth={2.5}
                fill="url(#incomeGradient)"
                activeDot={{
                  r: 5,
                  fill: '#10b981',
                  stroke: '#ffffff',
                  strokeWidth: 2,
                }}
              />
              <Line
                type="monotone"
                dataKey="buffer"
                name="Buffer Generated"
                stroke="#22d3ee"
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 4,
                  fill: '#22d3ee',
                  stroke: '#ffffff',
                  strokeWidth: 2,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default IncomeTrendChart;
